<template>
<div class="chapter">
<div class="inner_page">
<div class="chapter" id="chapter2">
<h1><a id="page25"></a><a href="#/007toc#ch02">2</a></h1>
<p class="noindent first">I’<span class="small">M NOT THE ONLY ONE TO HIT ROCK BOTTOM</span>. A<span class="small">PPARENTLY, MANY</span> Christians experience an initial excitement upon accepting Christ but later become disappointed, disillusioned, or even depressed.</p>
<p class="indent">Some of America’s church leaders have been trying to figure out why this epidemic exists and what can be done about it. In 2004, Willow Creek Community Church in South Barrington, Illinois, developed the REVEAL survey to understand and measure the heart—the emotions and attitudes—of the people who attend Willow Creek. Since then, over four hundred churches of a cross section of sizes, denominations, and regions of the country have used the survey to ask their members questions similar to the ones below.</p>
<p class="indent">We’ll get into how Christians around the country responded to questions like these. But first, take a moment to decide how you’d answer them. For each question, highlight a number between 1 (lowest level) and 10 (highest level) to indicate your answer.</p>
<p class="indent"><b>1.</b> How would you rate your level of enthusiasm for church?</p>
<div class="table" id="table1">
<table>
<tbody><tr>
<td>1</td>
<td>2</td>
<td>3</td>
<td>4</td>
<td>5</td>
<td>6</td>
<td>7</td>
<td>8</td>
<td>9</td>
<td>10</td>
</tr>
</tbody></table>
</div>
<p class="indent"><b>2.</b> How would you rate your overall level of fulfillment in life?</p>
<div class="table" id="table2">
<table>
<tbody><tr>
<td>1</td>
<td>2</td>
<td>3</td>
<td>4</td>
<td>5</td>
<td>6</td>
<td>7</td>
<td>8</td>
<td>9</td>
<td>10</td>
</tr>
</tbody></table>
</div>
<p class="indent"><a id="page26"></a><b>3.</b> How would you rate your level of satisfaction with your spiritual growth?</p>
<div class="table" id="table3">
<table>
<tbody><tr>
<td>1</td>
<td>2</td>
<td>3</td>
<td>4</td>
<td>5</td>
<td>6</td>
<td>7</td>
<td>8</td>
<td>9</td>
<td>10</td>
</tr>
</tbody></table>
</div>
<p class="indent"><b>4.</b> How would you rate your level of involvement in church-related activities?</p>
<div class="table" id="table4">
<table>
<tbody><tr>
<td>1</td>
<td>2</td>
<td>3</td>
<td>4</td>
<td>5</td>
<td>6</td>
<td>7</td>
<td>8</td>
<td>9</td>
<td>10</td>
</tr>
</tbody></table>
</div>
<h3>T<span class="small">HE</span> B<span class="small">IG</span> S<span class="small">URPRISE</span></h3>
<p class="noindent spacer">The Willow Creek researchers thought they might find a strong relationship between time spent on church activities and spiritual growth and fulfillment. They presumed that anyone who donated their time to church must be actively growing and fulfilled. That makes sense, right?</p>
<p class="indent">As it turns out, wrong.</p>
<p class="indent">The survey revealed that it wasn’t the more active Christians who were growing and fulfilled. The survey also unveiled a large number—approximately 25 percent of the attendees surveyed at Willow Creek—who admitted they were “stagnant” or “dissatisfied. ” And other churches are finding this to be true among their attendees as well.</p>
<p class="indent">So what’s going wrong in churches today? If we spend more time in church, shouldn’t we expect to grow spiritually and find fulfillment? Aren’t we told that if we drink the living water that Jesus offers, we’ll never thirst again? If this is true, then what’s happening with so many Christians today? What’s missing?</p>
<p class="indent">Many of our North American churches seem to have everything—culturally relevant outreach, attractive facilities, and a broad range of programs to match any and every lifestyle. Add to this the experience of dynamic speakers, professional-quality music, and inviting small groups. How could those who are most active in these churches be stagnant and dissatisfied?</p>
<div class="sidebar-right" id="sidebar1">
<p class="center"><a id="page27"></a><b>Many of today’s<br/>churches seem<br/>to have everything.</b></p>
</div>
<p class="indent">There’s nothing wrong with top-quality facilities, creative programs, and a genuine sense of community. But the fundamental question is, “What message are we sharing in our community and within our walls through our programs?” I believe it’s our <i>substance,</i> not our structure, that is leaving so many stagnant and dissatisfied. A church may have polished programs, well-trained staff, and dynamic speakers.</p>
<p class="indent">But <i>content</i> is what people walk away with.</p>
<h3>T<span class="small">HE</span> N<span class="small">AKED</span> G<span class="small">OSPEL</span> Q<span class="small">UIZ</span></h3>
<p class="noindent spacer">To illustrate this point, let’s pause for a short quiz. Below are ten faith-related concepts that don’t seem to be regularly discussed in many churches today. But our view on each of these concepts affects our relationship with God, our spiritual growth, and our fulfillment in life. So for each of the ten concepts, decide whether you think each is true or false. Simply highlight the <i>T</i> or <i>F</i> next to each statement to indicate your response. <i>(Note: Don’t move on to read the answers until you’ve responded to each one.)</i></p>
<div class="table" id="table5">
<table>
<tbody><tr>
<td><b>1.</b></td>
<td>Christians should ask God to forgive and cleanse them when they sin.</td>
<td>T</td>
<td>F</td>
</tr>
<tr>
<td><b>2.</b></td>
<td>Christians struggle with sin because of their old self within.</td>
<td>T</td>
<td>F</td>
</tr>
<tr>
<td><b>3.</b></td>
<td>We should wait on God even before making everyday decisions.</td>
<td>T</td>
<td>F</td>
</tr>
<tr>
<td><b>4.</b></td>
<td>When we sin against God, we’re out of fellowship until we repent.</td>
<td>T</td>
<td>F</td>
</tr>
<tr>
<td><b>5.</b></td>
<td>Old Testament law is written on Christians’ hearts so we want to obey it.</td>
<td>T</td>
<td>F</td>
</tr>
<tr>
<td><a id="page28"></a><b>6.</b></td>
<td>The Bible tells us that Christians can obtain many rewards in heaven.</td>
<td>T</td>
<td>F</td>
</tr>
<tr>
<td><b>7.</b></td>
<td>Christians will give an account for their sins at the great white throne.</td>
<td>T</td>
<td>F</td>
</tr>
<tr>
<td><b>8.</b></td>
<td>Christians should tithe at least 10 percent of their income to the church.</td>
<td>T</td>
<td>F</td>
</tr>
<tr>
<td><b>9.</b></td>
<td>God gets angry with us when we repeatedly sin against him.</td>
<td>T</td>
<td>F</td>
</tr>
<tr>
<td><b>10.</b></td>
<td>God looks at us as though we’re righteous, even though we’re really not.</td>
<td>T</td>
<td>F</td>
</tr>
</tbody></table>
</div>
<h3>Q<span class="small">UIZ</span> A<span class="small">NSWERS</span></h3>
<p class="noindent spacer">Why the quiz? Well, remember the survey you took at the beginning of this chapter? You assessed your <i>feelings</i> about church, your <i>enthusiasm</i> for life, and your <i>satisfaction</i> with your spiritual growth. Inevitably, our thinking leads to feelings. So the only effective way to move toward growth and fulfillment when we’re feeling dissatisfied or inexplicably stagnant is to dig deeply into God’s Word to find real answers that change our <i>thinking.</i></p>
<p class="indent">I lived through the consequences of my thinking, and my recovery came through a decade of slowly learning to replace old thoughts with new ones. I don’t know if there is more of a story to tell than my trial and error, suffering under error, and finally getting answers.</p>
<p class="indent">Speaking of answers, the biblical answer to each of the naked gospel quiz statements is <i>false.</i></p>
<p class="indent">Yes, <i>false.</i></p>
<p class="indent">So how’d you do?</p>
<p class="indent">Are you ready to peel away layers of religiosity in order to discover an exhilarating reality—always keeping in mind that truth is supposed to set you <i>free?</i></p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 012chapter2.vue?42c76d3b